import type { Asset } from '@mg/shared';
import { ImageOff, Play } from 'lucide-react';
import { useProxyImage } from '../../lib/useProxyImage';
import { cn } from '../../lib/cn';
import { Spinner } from '../../components/ui/Spinner';
import { mediaTypeIcon } from './mediaDisplay';

/**
 * Media thumbnail. Ready images are fetched through the auth image-proxy (so the bearer token is
 * attached) and rendered as an `<img>`. A video renders its stored POSTER — a still frame captured
 * in the browser at upload time — through that same proxy; the video itself is still never streamed
 * into the grid, which is the whole point of having a poster. Audio, and any video without one, show
 * a type icon. Assets still being generated show a spinner tile, and failed generations show an
 * error tile — neither has a blob to display yet.
 */
export function MediaThumb({ asset, className }: { asset: Asset; className?: string }) {
  /**
   * Anything ready that has a picture to show.
   *
   * Keyed off the THUMBNAIL rather than the media type, because a .pptx is not an image and now has
   * one anyway: its first slide is rendered at upload. Gating on `mediaType === 'image'` left a
   * PowerPoint deck drawing a generic file icon next to a thumbnail that had already been made for
   * it.
   */
  const isReadyImage =
    asset.status === 'ready' && (asset.mediaType === 'image' || Boolean(asset.thumbUrl));
  const isVideo = asset.mediaType === 'video';
  // A poster is a small JPEG, so fetching it is the same cost as an image thumbnail. The video's own
  // blob is still never touched here.
  const posterUrl = asset.status === 'ready' ? asset.posterUrl : null;
  // The 300px rendition, never the original. A grid of full-size images downloads the whole library
  // to draw 200px tiles — the reason this page was slow. `thumbUrl` is null for anything without
  // one (older uploads until the sweep catches them, SVG, already-small files), and then the
  // original is exactly what it always was.
  const src = useProxyImage(isReadyImage ? (asset.thumbUrl ?? asset.blobUrl) : posterUrl);
  const Icon = mediaTypeIcon[asset.mediaType];

  const tile = (children: React.ReactNode) => (
    <div
      className={cn(
        'flex flex-col items-center justify-center gap-1.5 overflow-hidden bg-page text-ink-ghost',
        className,
      )}
    >
      {children}
    </div>
  );

  if (asset.status === 'generating') {
    return tile(
      <>
        <Spinner className="h-6 w-6 text-action" />
        <span className="text-xs font-medium text-ink-faint">Generating&hellip;</span>
      </>,
    );
  }

  if (asset.status === 'failed') {
    return (
      <div
        className={cn(
          'flex flex-col items-center justify-center gap-1.5 overflow-hidden bg-red-50 dark:bg-error-dark-tint text-red-400 dark:text-error-dark',
          className,
        )}
      >
        <ImageOff className="h-7 w-7" aria-hidden />
        <span className="text-xs font-medium text-red-600 dark:text-error-dark">Generation failed</span>
      </div>
    );
  }

  return (
    <div
      className={cn(
        'relative flex items-center justify-center overflow-hidden bg-page text-ink-ghost',
        className,
      )}
    >
      {src ? (
        <>
          {/* No onError latch needed: useProxyImage resolves to null on a failed fetch, so a poster
              whose blob has gone missing falls back to the icon on its own rather than showing a
              broken-image box. White behind the picture itself, so a transparent logo or graphic
              stays legible on a dark tile; an opaque picture covers it completely. */}
          <img src={src} alt="" loading="lazy" className="h-full w-full bg-white object-cover" />
          {isVideo && <PlayBadge />}
        </>
      ) : (
        <Icon className="h-8 w-8" aria-hidden />
      )}
    </div>
  );
}

/**
 * The "this is a video" mark, laid over a poster.
 *
 * Only ever drawn over a PICTURE. A video with no poster still shows the film icon, which says the
 * same thing on its own — stacking a play button on top of it would be two icons saying one thing.
 *
 * Shared with the composer's media tiles so a video looks like a video everywhere it is picked.
 */
export function PlayBadge() {
  return (
    // aria-hidden throughout: it is a picture of a play button, not a control and not a label. As
    // real text it became the accessible NAME of the tile button in the composer's picker, so every
    // video there announced as "Video" instead of its filename.
    <span
      className="pointer-events-none absolute inset-0 flex items-center justify-center"
      aria-hidden
    >
      {/* Sized as a FRACTION of the tile, not a fixed 36px: the upload row's thumbnails are 40px,
          where a fixed disc covered the whole frame it was supposed to annotate. */}
      <span className="flex h-2/5 max-h-9 min-h-4 w-2/5 max-w-9 min-w-4 items-center justify-center rounded-full bg-black/55 ring-1 ring-white/30">
        {/* Nudged right by a pixel: a triangle centred by its bounding box reads as sitting left. */}
        <Play className="h-1/2 w-1/2 translate-x-[1px] fill-white text-white" aria-hidden />
      </span>
    </span>
  );
}
